// src/components/Chat/ContactList.jsx
import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../context/AuthContext";

import AddIcon from "@mui/icons-material/Add";
import Avatar from "@mui/material/Avatar";
import GroupIcon from "@mui/icons-material/Groups";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { useNavigate } from "react-router-dom";

import "./ContactList.css";

function normalizePhoneNumber(input, defaultCountry = "+91") {
  if (!input) return "";
  let num = input.replace(/[^0-9+]/g, "");
  if (num.startsWith("+") && num.length > 8) return num;
  if (num.startsWith("00")) return "+" + num.substring(2);
  if (!num.startsWith("+") && num.length === 10) return defaultCountry + num;
  return num.startsWith("+") ? num : defaultCountry + num;
}

export default function ContactList({ onSelectChat, selectedChat }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPhone, setNewPhone] = useState("");

  // Load both 1:1 chats and groups, merge & sort by lastMessageTime
  useEffect(() => {
    if (!currentUser?.uid) return;

    let unsubChats = () => {};
    let mounted = true;

    const init = async () => {
      const qChats = query(
        collection(db, "chats"),
        where("users", "array-contains", currentUser.uid)
      );

      unsubChats = onSnapshot(qChats, async (snapshot) => {
        // Build 1:1 chats
        const chatList = await Promise.all(
          snapshot.docs.map(async (snapDoc) => {
            const chat = snapDoc.data();
            if (!Array.isArray(chat.users) || chat.users.length < 2) return null;

            // find other user id
            const otherUid = chat.users.find((u) => u !== currentUser.uid);
            if (!otherUid) return null;

            const userSnap = await getDoc(doc(db, "users", otherUid));
            const userData = userSnap.exists()
              ? userSnap.data()
              : { name: "Unknown", email: "Unknown" };

            return {
              chatId: snapDoc.id,
              uid: otherUid,
              isGroup: false,
              name: userData.name || userData.email,
              photoURL: userData.photoURL || "",
              lastMessage: chat.lastMessage || "",
              lastMessageTime: chat.lastMessageTime?.toDate?.() || null,
              unreadCount:
                (chat.unreadCount && chat.unreadCount[currentUser.uid]) || 0,
            };
          })
        );

        const cleanedChats = chatList.filter(Boolean);

        // Load groups where user is member (one-time per snapshot update)
        const qGroups = query(
          collection(db, "groups"),
          where("members", "array-contains", currentUser.uid)
        );

        const groupSnaps = await getDocs(qGroups);
        const groupList = groupSnaps.docs.map((g) => {
          const d = g.data();
          // accept both group.name and group.groupName fields
          const displayName = d.name || d.groupName || "Unnamed Group";
          return {
            chatId: g.id,
            uid: g.id,
            isGroup: true,
            name: displayName,
            photoURL: d.photoURL || "", // optional
            lastMessage: d.lastMessage || "",
            lastMessageTime: d.lastMessageTime?.toDate?.() || null,
            unreadCount:
              (d.unreadCount && d.unreadCount[currentUser.uid]) || 0,
          };
        });

        // Merge + sort by lastMessageTime desc (fallback to createdAt if needed)
        const finalList = [...cleanedChats, ...groupList];
        finalList.sort((a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0));

        if (mounted) {
          setContacts(finalList);
          setLoading(false);
        }
      });
    };

    init();

    return () => {
      mounted = false;
      try { unsubChats(); } catch (e) {}
    };
  }, [currentUser]);

  // Add contact manually by phone
  const handleAddContact = async () => {
    if (!newPhone.trim()) return alert("Enter a phone number");

    const normalized = normalizePhoneNumber(newPhone);

    try {
      const usersRef = collection(db, "users");
      const qUser = query(usersRef, where("phone", "==", normalized));
      const results = await getDocs(qUser);

      if (results.empty) {
        alert("No user exists with this number: " + normalized);
        return;
      }

      const otherDoc = results.docs[0];
      const otherUid = otherDoc.id;

      if (otherUid === currentUser.uid) return alert("You cannot add yourself");

      const chatId = [currentUser.uid, otherUid].sort().join("_");
      const chatRef = doc(db, "chats", chatId);

      await setDoc(
        chatRef,
        {
          users: [currentUser.uid, otherUid],
          createdAt: serverTimestamp(),
          lastMessage: "",
          lastMessageTime: null,
          unreadCount: {
            [currentUser.uid]: 0,
            [otherUid]: 0,
          },
        },
        { merge: true }
      );

      setNewPhone("");
    } catch (err) {
      console.error(err);
      alert("Error adding contact");
    }
  };

  // Select chat or group (reset unread)
  const handleSelect = async (contact) => {
    if (onSelectChat) onSelectChat(contact);

    try {
      if (!contact.isGroup) {
        await updateDoc(doc(db, "chats", contact.chatId), {
          [`unreadCount.${currentUser.uid}`]: 0,
        });
      } else {
        await updateDoc(doc(db, "groups", contact.chatId), {
          [`unreadCount.${currentUser.uid}`]: 0,
        });
      }
    } catch (err) {
      console.error("Failed to reset unread count", err);
    }
  };

  return (
    <div className="contact-list">
      <div className="contact-list-header">
        <h3>Chats</h3>

        <button
          className="create-group-btn"
          onClick={() => navigate("/groups/create")}
          title="Create New Group"
        >
          <GroupAddIcon style={{ fontSize: 28, color: "gold" }} />
        </button>

        <div className="add-contact-box">
          <input
            type="text"
            placeholder="Phone number (e.g. +919876543210)"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
          />
          <button className="add-btn" onClick={handleAddContact}>
            <AddIcon />
          </button>
        </div>
      </div>

      {loading ? (
        <p className="loading">Loading chats...</p>
      ) : contacts.length === 0 ? (
        <p className="no-chats">No chats yet.</p>
      ) : (
        <div className="contact-items">
          {contacts.map((c) => (
            <div
              key={c.chatId}
              className={`contact-item ${selectedChat?.chatId === c.chatId ? "active" : ""}`}
              onClick={() => handleSelect(c)}
            >
              <Avatar
                src={c.photoURL || ""}
                style={{
                  backgroundColor: c.isGroup ? "#2e7d32" : undefined,
                }}
              >
                {c.isGroup ? <GroupIcon /> : (c.name ? c.name[0].toUpperCase() : "?")}
              </Avatar>

              <div className="contact-info">
                <div style={{ color: c.isGroup ? "#00e676" : "gold", fontWeight: 600 }}>
                  {c.name}
                </div>
                {c.lastMessage && <div className="contact-lastmsg">{c.lastMessage}</div>}
              </div>

              {c.unreadCount > 0 && <span className="unread-badge">{c.unreadCount}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
